import { Expo } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { gsap } from 'gsap/dist/gsap';
import Image from 'next/image';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import styles from './business-hub-solutions.module.scss';
import classNames from 'classnames';

export interface Slide {
    title: string;
    description: string;
    image?: string;
    imageRatio?: number;
    imagePosition?: 'center' | 'bottom';
    imageBordered?: boolean;
}

export type Props = {
    title: string;
    slides: Slide[];
};

gsap.registerPlugin(ScrollTrigger);

const BusinessHubSolutions: FC<Props> = (props) => {
    const { title, slides = [] } = props;

    const [isRendered, setIsRendered] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const sections = useRef<HTMLElement[]>([]);

    useEffect(() => {
        () => {
            sections.current = [];
        };
    }, []);

    const ctx = useRef<gsap.Context>();
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        if (!sections.current.length) return;
        if (isRendered) return;
        setIsRendered(true);

        ctx.current = gsap.context(() => {
            let tl = gsap.timeline({
                defaults: { duration: 2, ease: Expo.easeOut },
                scrollTrigger: {
                    trigger: containerRef.current,
                    pinnedContainer: containerRef.current,
                    anticipatePin: 1,
                    pin: true,
                    start: () => {
                        const containerHeight = containerRef.current?.getBoundingClientRect().height || 0;
                        const windowHeight = window.innerHeight;

                        if (containerHeight < windowHeight) {
                            return 'center center';
                        }

                        return 'bottom bottom';
                    },
                    scrub: true,
                    end: '+=300%',
                    invalidateOnRefresh: true,
                },
            });

            gsap.set(sections.current, { autoAlpha: 1, yPercent: (index) => (index === 0 ? 0 : 100), xPercent: (index) => -100 * index });

            for (let index = 1; index < sections.current.length; index++) {
                const section = sections.current[index];
                const prevSection = sections.current[index - 1];

                tl.to(
                    [section],
                    {
                        yPercent: 0,
                        opacity: 1,
                    },
                    '+=2'
                ).to(
                    [prevSection],
                    {
                        yPercent: -50 * index,
                        opacity: 0,
                    },
                    '<'
                );
            }
        });
    }, [containerRef, sections]);

    return (
        <div className={styles.container}>
            <div ref={containerRef}>
                <div className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }} />
                <div className={styles.contentBox}>
                    {/* <div className={styles.contentOverlay}>
                        <div className={styles.overlayLeft}></div>
                    </div> */}

                    <div className={styles.parallaxContainer}>
                        {slides?.map(({ title, description, image, imageRatio, imagePosition = 'center', imageBordered }, idx) => (
                            <section
                                key={`section-${idx}`}
                                className={classNames(styles.section, styles[imagePosition])}
                                ref={(ref) => {
                                    if (ref) sections.current.push(ref);
                                }}
                            >
                                <div className={classNames(styles.sectionLeft, styles[imagePosition])}>
                                    <div className={classNames(styles.image, styles[imagePosition], { [styles.bordered]: imageBordered })} style={{ aspectRatio: `${imageRatio}` }}>
                                        <Image
                                            src={image || ''}
                                            alt=""
                                            style={{ objectFit: 'contain', objectPosition: 'center' }}
                                            fill={true}
                                            loading={'eager'}
                                            loader={({ src, width, quality }) => {
                                                return `${src}?width=${width}&quality=${quality ?? 75}`;
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.sectionRight}>
                                    <div className={styles.number}>
                                        <span>{idx + 1}</span>
                                    </div>
                                    <div className={styles.content}>
                                        <h1 className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }}></h1>

                                        <div className={styles.description} dangerouslySetInnerHTML={{ __html: description || '' }} />
                                        <div className={classNames(styles.imageMobile, { [styles.bordered]: imageBordered })} style={{ aspectRatio: `${imageRatio}` }}>
                                            <Image
                                                src={image || ''}
                                                alt=""
                                                style={{ objectFit: 'contain', objectPosition: 'center' }}
                                                fill={true}
                                                loading={'eager'}
                                                loader={({ src, width, quality }) => {
                                                    return `${src}?width=${width}&quality=${quality ?? 75}`;
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BusinessHubSolutions;
